<div class="selection-criteria-builder">
    <!-- Loading Overlay -->
    <div *ngIf="busy" class="flex justify-content-center align-items-center p-4">
        <i class="pi pi-spin pi-spinner text-4xl"></i>
    </div>

    <!-- Header Toolbar -->
    <div class="flex justify-content-between align-items-center mb-3">
        <div class="flex align-items-center gap-2">
            <span class="text-lg font-semibold">Selektionskriterien-Builder</span>
            <p-tag *ngIf="criteriaTree?.isValid" severity="success" value="Gültig"></p-tag>
            <p-tag *ngIf="criteriaTree && !criteriaTree.isValid" severity="danger" value="Ungültig"></p-tag>
        </div>
        <div class="flex gap-2" *ngIf="!readOnly">
            <button pButton type="button" label="Gruppe hinzufügen" icon="fa-solid fa-plus"
                    class="p-button-outlined p-button-sm"
                    (click)="addRootGroup()"
                    [disabled]="busy">
            </button>
            <button pButton type="button" label="Vorschau" icon="fa-solid fa-eye"
                    class="p-button-outlined p-button-sm p-button-info"
                    (click)="executePreview()"
                    [disabled]="busy || !criteriaTree?.rootGroups?.length">
            </button>
            <button pButton type="button" label="Validieren" icon="fa-solid fa-check"
                    class="p-button-outlined p-button-sm p-button-warning"
                    (click)="validateCriteria()"
                    [disabled]="busy || !criteriaTree?.rootGroups?.length">
            </button>
            <button pButton type="button" icon="fa-solid fa-code-branch"
                    class="p-button-outlined p-button-sm"
                    pTooltip="Versionshistorie"
                    (click)="openVersionHistory()"
                    [disabled]="busy || !criteriaTree?.id">
            </button>
            <p-splitbutton  label="Speichern" icon="fa-solid fa-save"
                           class="p-button-sm p-button-success"
                           [model]="splitButtonOptions"
                           (onClick)="saveCriteria()"
                           [disabled]="busy">
            </p-splitbutton>
        </div>
    </div>

    <!-- Empty State -->
    <div *ngIf="initialized && (!criteriaTree?.rootGroups || criteriaTree.rootGroups.length === 0)"
         class="flex flex-column align-items-center justify-content-center p-6 border-1 border-dashed border-300 border-round">
        <i class="fa-solid fa-filter text-4xl text-300 mb-3"></i>
        <span class="text-600 mb-3">Keine Selektionskriterien definiert</span>
        <button *ngIf="!readOnly" pButton type="button" label="Regelgruppe hinzufügen" icon="fa-solid fa-plus"
                class="p-button-outlined"
                (click)="addRootGroup()">
        </button>
    </div>

    <!-- Rule Groups Tree -->
    <div *ngIf="criteriaTree?.rootGroups?.length > 0" class="rule-groups-container">
        <ng-container *ngFor="let group of criteriaTree.rootGroups; let i = index; trackBy: trackByGroupId">
            <!-- Set Operation Badge for groups after the first -->
            <div *ngIf="i > 0" class="flex justify-content-center my-2">
                <p-select *ngIf="!readOnly"
                            [options]="[
                                {label: 'Vereinigung (Hinzufügen)', value: SetOperationEnum.union},
                                {label: 'Schnittmenge', value: SetOperationEnum.intersect},
                                {label: 'Ausnahme (Entfernen)', value: SetOperationEnum.except}
                            ]"
                            [(ngModel)]="group.setOperation"
                            (onChange)="updateGroup(group)"
                            optionLabel="label"
                            optionValue="value"
                            styleClass="p-dropdown-sm">
                </p-select>
                <p-tag *ngIf="readOnly" [value]="getSetOperationLabel(group.setOperation)"
                       [severity]="group.setOperation === SetOperationEnum.except ? 'danger' : 'info'">
                </p-tag>
            </div>

            <!-- Rule Group Card -->
            <ng-container *ngTemplateOutlet="ruleGroupTemplate; context: {group: group, level: 0}"></ng-container>
        </ng-container>
    </div>

    <!-- Preview Results Panel -->
    <div *ngIf="previewResult" class="mt-4">
        <p-panel [toggleable]="true">
            <ng-template pTemplate="header">
                <div class="flex align-items-center gap-2">
                    <span class="font-semibold">Vorschau-Ergebnisse</span>
                    <p-tag *ngIf="previewResult.previewStatus === 'OK'" severity="success" value="OK"></p-tag>
                    <p-tag *ngIf="previewResult.previewStatus === 'EMPTY'" severity="danger" value="Leer"></p-tag>
                    <p-tag *ngIf="previewResult.previewStatus === 'LOW_COUNT'" severity="warn" value="Wenige"></p-tag>
                    <p-tag *ngIf="previewResult.previewStatus === 'HIGH_COUNT'" severity="info" value="Viele"></p-tag>
                    <p-tag *ngIf="previewResult.previewStatus === 'EXCESSIVE'" severity="danger" value="Übermäßig"></p-tag>
                    <p-tag *ngIf="previewResult.previewStatus === 'SLOW'" severity="warn" value="Langsam"></p-tag>
                </div>
            </ng-template>
            <div class="grid">
                <!-- Summary Statistics -->
                <div class="col-12 md:col-6">
                    <div class="flex flex-column gap-2">
                        <div class="flex justify-content-between">
                            <span class="text-500">Geschätzte Anzahl:</span>
                            <span class="font-semibold" [ngClass]="{
                                'text-red-500': previewResult.previewStatus === 'EMPTY' || previewResult.previewStatus === 'EXCESSIVE',
                                'text-yellow-600': previewResult.previewStatus === 'LOW_COUNT' || previewResult.previewStatus === 'HIGH_COUNT',
                                'text-green-600': previewResult.previewStatus === 'OK'
                            }">{{ previewResult.estimatedCount | number }}</span>
                        </div>
                        <div class="flex justify-content-between">
                            <span class="text-500">Ausführungszeit:</span>
                            <span [ngClass]="{'text-yellow-600': previewResult.executionTimeMs > 5000}">
                                {{ previewResult.executionTimeMs }}ms
                            </span>
                        </div>
                        <div class="flex justify-content-between" *ngIf="previewResult.sampleRecords?.length">
                            <span class="text-500">Beispieldatensätze:</span>
                            <span>{{ previewResult.sampleRecords.length }}</span>
                        </div>
                    </div>
                </div>

                <!-- Preview Warnings -->
                <div class="col-12" *ngIf="previewResult.warnings?.length">
                    <p-divider></p-divider>
                    <div class="flex flex-column gap-2">
                        <div *ngFor="let warning of previewResult.warnings"
                             class="flex align-items-start gap-2 p-2 border-round"
                             [ngClass]="{
                                'surface-100': warning.severity === 'INFO',
                                'bg-yellow-50': warning.severity === 'WARNING',
                                'bg-red-50': warning.severity === 'ERROR'
                             }">
                            <i class="mt-1" [ngClass]="{
                                'fa-solid fa-info-circle text-blue-500': warning.severity === 'INFO',
                                'fa-solid fa-exclamation-triangle text-yellow-600': warning.severity === 'WARNING',
                                'fa-solid fa-exclamation-circle text-red-500': warning.severity === 'ERROR'
                            }"></i>
                            <div class="flex flex-column">
                                <span class="font-medium">{{ warning.message }}</span>
                                <span *ngIf="warning.suggestion" class="text-500 text-sm">
                                    {{ warning.suggestion }}
                                </span>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Sample Records Table -->
                <div class="col-12" *ngIf="previewResult.sampleRecords?.length">
                    <p-divider></p-divider>
                    <div class="flex justify-content-between align-items-center mb-2">
                        <span class="font-semibold">Beispieldatensätze</span>
                        <span class="text-500 text-sm">Klicken Sie auf eine Zeile für Details</span>
                    </div>
                    <p-table [value]="previewResult.sampleRecords"
                             [paginator]="previewResult.sampleRecords.length > 10"
                             [rows]="10"
                             styleClass="p-datatable-sm p-datatable-striped"
                             selectionMode="single"
                             (onRowSelect)="onSampleRecordSelect($event)">
                        <ng-template pTemplate="header">
                            <tr>
                                <th style="width: 50px">#</th>
                                <th *ngIf="targetType === SelectionTargetTypeEnum.customer" style="width: 100px">ID</th>
                                <th *ngIf="targetType === SelectionTargetTypeEnum.contract" style="width: 120px">Vertrag Nr.</th>
                                <th>Name</th>
                                <th style="width: 100px">Status</th>
                                <th style="width: 50px"></th>
                            </tr>
                        </ng-template>
                        <ng-template pTemplate="body" let-sample let-rowIndex="rowIndex">
                            <tr [pSelectableRow]="sample" class="cursor-pointer">
                                <td>{{ rowIndex + 1 }}</td>
                                <td *ngIf="targetType === SelectionTargetTypeEnum.customer">
                                    <span class="text-primary font-medium">{{ sample.customerId }}</span>
                                </td>
                                <td *ngIf="targetType === SelectionTargetTypeEnum.contract">
                                    <span class="text-primary font-medium">{{ sample.contractNumber || sample.contractId }}</span>
                                </td>
                                <td>
                                    <span *ngIf="targetType === SelectionTargetTypeEnum.customer">{{ sample.customerName }}</span>
                                    <span *ngIf="targetType === SelectionTargetTypeEnum.contract">{{ sample.customerName }}</span>
                                </td>
                                <td>
                                    <p-tag [value]="sample.customerStatus || sample.contractStatus || 'N/A'"
                                           [severity]="(sample.customerStatus || sample.contractStatus) === 'ACTIVE' ? 'success' : 'warn'"
                                           styleClass="text-xs">
                                    </p-tag>
                                </td>
                                <td class="text-center">
                                    <button pButton type="button" icon="fa-solid fa-eye"
                                            class="p-button-text p-button-sm p-button-rounded"
                                            pTooltip="Details anzeigen"
                                            (click)="viewSampleRecordDetails(sample, $event)">
                                    </button>
                                </td>
                            </tr>
                        </ng-template>
                        <ng-template pTemplate="emptymessage">
                            <tr>
                                <td colspan="5" class="text-center text-500 p-4">
                                    Keine Beispieldatensätze verfügbar
                                </td>
                            </tr>
                        </ng-template>
                    </p-table>
                </div>
            </div>
        </p-panel>
    </div>

    <!-- Sample Record Detail Dialog -->
    <p-dialog [(visible)]="showSampleDetailDialog"
              [header]="sampleDetailHeader"
              [modal]="true"
              [style]="{width: '600px'}"
              [closable]="true"
              [dismissableMask]="true">
        <div *ngIf="selectedSampleRecord" class="flex flex-column gap-3">
            <!-- Primary Info -->
            <div class="grid">
                <div class="col-6">
                    <span class="text-500 text-sm block mb-1">
                        {{ targetType === SelectionTargetTypeEnum.customer ? 'Kunden-ID' : 'Vertrags-ID' }}
                    </span>
                    <span class="font-semibold text-lg">
                        {{ targetType === SelectionTargetTypeEnum.customer ? selectedSampleRecord.customerId : selectedSampleRecord.contractId }}
                    </span>
                </div>
                <div class="col-6">
                    <span class="text-500 text-sm block mb-1">Status</span>
                    <p-tag [value]="selectedSampleRecord.customerStatus || selectedSampleRecord.contractStatus || 'N/A'"
                           [severity]="(selectedSampleRecord.customerStatus || selectedSampleRecord.contractStatus) === 'ACTIVE' ? 'success' : 'warn'">
                    </p-tag>
                </div>
            </div>

            <p-divider></p-divider>

            <!-- All Data Fields -->
            <div>
                <span class="font-semibold mb-2 block">Datensatzdetails</span>
                <div class="grid">
                    <div class="col-6" *ngFor="let field of getSampleDataFields(selectedSampleRecord)">
                        <div class="flex flex-column p-2 surface-50 border-round">
                            <span class="text-500 text-sm">{{ field.key | titlecase }}</span>
                            <span class="font-medium">{{ field.value }}</span>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Navigation Button -->
            <div class="flex justify-content-end gap-2 mt-2">
                <button pButton type="button" label="Schließen"
                        class="p-button-outlined"
                        (click)="showSampleDetailDialog = false">
                </button>
                <button pButton type="button" label="Vollständigen Datensatz öffnen"
                        icon="fa-solid fa-external-link-alt"
                        (click)="navigateToFullRecord(selectedSampleRecord)">
                </button>
            </div>
        </div>
    </p-dialog>

    <!-- Validation Results Panel -->
    <div *ngIf="validationResult" class="mt-4">
        <p-panel header="Validierungsergebnisse" [toggleable]="true">
            <div *ngIf="validationResult.isValid" class="text-green-600">
                <i class="fa-solid fa-check-circle mr-2"></i>
                Alle Kriterien sind gültig
            </div>
            <div *ngIf="!validationResult.isValid">
                <div *ngFor="let error of validationResult.errors" class="text-red-600 mb-2">
                    <i class="fa-solid fa-exclamation-circle mr-2"></i>
                    {{ error.message }}
                </div>
            </div>
            <div *ngIf="validationResult.warnings?.length" class="mt-2">
                <div *ngFor="let warning of validationResult.warnings" class="text-yellow-600 mb-2">
                    <i class="fa-solid fa-exclamation-triangle mr-2"></i>
                    {{ warning.message }}
                    <span *ngIf="warning.suggestion" class="text-500 ml-2">({{ warning.suggestion }})</span>
                </div>
            </div>
        </p-panel>
    </div>
</div>



<!-- Rule Group Template (Recursive) -->
<ng-template #ruleGroupTemplate let-group="group" let-level="level">
    <div class="rule-group border-1 border-300 border-round p-3 mb-2"
         [ngClass]="getLevelColor(level)"
         [style.margin-left.px]="level * 24">

        <!-- Group Header -->
        <div class="flex justify-content-between align-items-center mb-2">
            <div class="flex align-items-center gap-2">
                <!-- Logical Operator Toggle -->
                <p-selectButton *ngIf="!readOnly"
                                [options]="[
                                    {label: 'AND', value: LogicalOperatorEnum.and},
                                    {label: 'OR', value: LogicalOperatorEnum.or}
                                ]"
                                [(ngModel)]="group.logicalOperator"
                                (onChange)="updateGroup(group)"
                                optionLabel="label"
                                optionValue="value"
                                styleClass="p-button-sm">
                </p-selectButton>
                <p-tag *ngIf="readOnly" [value]="getLogicalOperatorLabel(group.logicalOperator)"
                       [severity]="group.logicalOperator === LogicalOperatorEnum.and ? 'info' : 'warn'">
                </p-tag>

                <!-- Group Label -->
                <input *ngIf="!readOnly" type="text" pInputText
                       [(ngModel)]="group.label"
                       (blur)="updateGroup(group)"
                       placeholder="Gruppenbezeichnung (optional)"
                       class="p-inputtext-sm"
                       style="width: 200px;">
                <span *ngIf="readOnly && group.label" class="font-semibold">{{ group.label }}</span>

                <!-- Negation Toggle -->
                <p-toggleButton *ngIf="!readOnly"
                                [(ngModel)]="group.isNegated"
                                (onChange)="updateGroup(group)"
                                onLabel="NOT"
                                offLabel="NOT"
                                onIcon="fa-solid fa-check"
                                offIcon="fa-solid fa-times"
                                styleClass="p-button-sm p-button-danger">
                </p-toggleButton>
                <p-tag *ngIf="readOnly && group.isNegated" severity="danger" value="NOT"></p-tag>
            </div>

            <!-- Group Actions -->
            <div *ngIf="!readOnly" class="flex gap-1">
                <button pButton type="button" icon="fa-solid fa-plus"
                        class="p-button-text p-button-sm p-button-success"
                        pTooltip="Bedingung hinzufügen"
                        (click)="addConditionToGroup(group)">
                </button>
                <button pButton type="button" icon="fa-solid fa-layer-group"
                        class="p-button-text p-button-sm p-button-info"
                        pTooltip="Verschachtelte Gruppe hinzufügen"
                        (click)="addNestedGroup(group)">
                </button>
                <button pButton type="button" icon="fa-solid fa-ellipsis-v"
                        class="p-button-text p-button-sm"
                        (click)="showGroupMenu($event, group)">
                </button>
            </div>
        </div>

        <!-- Conditions -->
        <div *ngIf="group.conditions?.length" class="conditions-container mb-2">
            <ng-container *ngFor="let condition of group.conditions; let condIndex = index; trackBy: trackByConditionId">
                <!-- Logical Operator between conditions -->
                <div *ngIf="condIndex > 0" class="flex justify-content-start my-1 ml-4">
                    <span class="text-500 text-sm">{{ getLogicalOperatorLabel(group.logicalOperator) }}</span>
                </div>

                <!-- Condition Row -->
                <div class="condition-row flex align-items-center gap-2 p-2 surface-50 border-round">
                    <!-- Negation -->
                    <p-checkbox *ngIf="!readOnly"
                                [(ngModel)]="condition.isNegated"
                                (onChange)="updateCondition(condition)"
                                [binary]="true"
                                label="NOT"
                                styleClass="mr-2">
                    </p-checkbox>
                    <span *ngIf="readOnly && condition.isNegated" class="text-red-500 font-semibold mr-2">NOT</span>

                    <!-- Field Selection -->
                    <p-treeSelect *ngIf="!readOnly"
                                  [options]="fieldTreeNodes"
                                  [(ngModel)]="condition['_selectedField']"
                                  (onNodeSelect)="onFieldSelect(condition, $event)"
                                  placeholder="Feld auswählen"
                                  [filter]="true"
                                  filterPlaceholder="Felder suchen..."
                                  styleClass="w-12rem"
                                  appendTo="body">
                    </p-treeSelect>
                    <span *ngIf="readOnly" class="font-semibold">{{ condition.fieldPath }}</span>

                    <!-- Comparison Operator -->
                    <p-select *ngIf="!readOnly && condition.fieldPath"
                                [options]="getAllowedOperators(condition)"
                                [(ngModel)]="condition.comparisonOperator"
                                (onChange)="updateCondition(condition)"
                                styleClass="w-8rem">
                        <ng-template pTemplate="selectedItem">
                            {{ getOperatorLabel(condition.comparisonOperator) }}
                        </ng-template>
                        <ng-template let-op pTemplate="item">
                            {{ getOperatorLabel(op) }}
                        </ng-template>
                    </p-select>
                    <span *ngIf="readOnly" class="text-primary">{{ getOperatorLabel(condition.comparisonOperator) }}</span>

                    <!-- Value Input (based on field type and operator) -->
                    <ng-container *ngIf="condition.fieldPath && needsValue(condition.comparisonOperator)">
                        <!-- Enum dropdown -->
                        <p-select *ngIf="condition.fieldType === FieldTypeEnum.enum && !readOnly"
                                    [options]="getEnumValues(condition)"
                                    [(ngModel)]="condition.conditionValue"
                                    (onChange)="updateCondition(condition)"
                                    placeholder="Wert auswählen"
                                    styleClass="w-10rem"
                                    appendTo="body">
                        </p-select>

                        <!-- Text input -->
                        <input *ngIf="condition.fieldType === FieldTypeEnum.string && !readOnly"
                               type="text" pInputText
                               [(ngModel)]="condition.conditionValue"
                               (blur)="updateCondition(condition)"
                               placeholder="Wert eingeben"
                               class="w-10rem">

                        <!-- Number input -->
                        <p-inputNumber *ngIf="(condition.fieldType === FieldTypeEnum.integer || condition.fieldType === FieldTypeEnum.decimal) && !readOnly"
                                       [(ngModel)]="condition.conditionValue"
                                       (onBlur)="updateCondition(condition)"
                                       styleClass="w-8rem">
                        </p-inputNumber>

                        <!-- Date input -->
                        <p-datePicker *ngIf="(condition.fieldType === FieldTypeEnum.date || condition.fieldType === FieldTypeEnum.datetime) && !readOnly"
                                      [(ngModel)]="condition.conditionValue"
                                      (onSelect)="updateCondition(condition)"
                                      [showTime]="condition.fieldType === FieldTypeEnum.datetime"
                                      styleClass="w-10rem"
                                      appendTo="body">
                        </p-datePicker>

                        <!-- Boolean input -->
                        <p-select *ngIf="condition.fieldType === FieldTypeEnum.boolean && !readOnly"
                                    [options]="[{label: 'Wahr', value: 'true'}, {label: 'Falsch', value: 'false'}]"
                                    [(ngModel)]="condition.conditionValue"
                                    (onChange)="updateCondition(condition)"
                                    optionLabel="label"
                                    optionValue="value"
                                    styleClass="w-6rem"
                                    appendTo="body">
                        </p-select>

                        <!-- Display value in read-only mode -->
                        <span *ngIf="readOnly" class="font-semibold">{{ condition.conditionValue }}</span>
                    </ng-container>

                    <!-- Secondary value for BETWEEN -->
                    <ng-container *ngIf="condition.fieldPath && needsSecondValue(condition.comparisonOperator)">
                        <span class="text-500">und</span>
                        <input *ngIf="!readOnly" type="text" pInputText
                               [(ngModel)]="condition.conditionValueSecondary"
                               (blur)="updateCondition(condition)"
                               placeholder="Endwert"
                               class="w-8rem">
                        <span *ngIf="readOnly" class="font-semibold">{{ condition.conditionValueSecondary }}</span>
                    </ng-container>

                    <!-- Value list for IN/NOT IN -->
                    <ng-container *ngIf="condition.fieldPath && needsValueList(condition.comparisonOperator)">
                        <p-autoComplete *ngIf="!readOnly"
                                        [(ngModel)]="condition.conditionValueList"
                                        [multiple]="true"
                                        (onSelect)="updateCondition(condition)"
                                        (onUnselect)="updateCondition(condition)"
                                        (onKeyUp)="onValueListKeyUp($event, condition)"
                                        placeholder="Werte eingeben"
                                        styleClass="w-15rem">
                        </p-autoComplete>
                        <span *ngIf="readOnly" class="font-semibold">{{ condition.conditionValueList?.join(', ') }}</span>
                    </ng-container>

                    <!-- Delete button -->
                    <button *ngIf="!readOnly" pButton type="button" icon="fa-solid fa-trash"
                            class="p-button-text p-button-sm p-button-danger ml-auto"
                            (click)="deleteCondition(condition)">
                    </button>
                </div>
            </ng-container>
        </div>

        <!-- Child Groups (Recursive) -->
        <ng-container *ngIf="group.childGroups?.length">
            <ng-container *ngFor="let childGroup of group.childGroups; trackBy: trackByGroupId">
                <ng-container *ngTemplateOutlet="ruleGroupTemplate; context: {group: childGroup, level: level + 1}"></ng-container>
            </ng-container>
        </ng-container>

        <!-- Empty Group Message -->
        <div *ngIf="!group.conditions?.length && !group.childGroups?.length"
             class="text-center text-500 p-2">
            <i class="fa-solid fa-info-circle mr-2"></i>
            Leere Gruppe - Bedingungen oder verschachtelte Gruppen hinzufügen
        </div>
    </div>
</ng-template>

<!-- Context Menus -->
<p-menu #groupMenu [popup]="true" [model]="groupMenuItems" appendTo="body"></p-menu>
<p-menu #conditionMenu [popup]="true" [model]="conditionMenuItems" appendTo="body"></p-menu>

<!-- Version History Component (F-016) -->
<ct-selection-criteria-version-history
    [selectionCriteriaId]="criteriaTree?.id"
    [(visible)]="showVersionHistory"
    (onRollback)="onVersionRollback()">
</ct-selection-criteria-version-history>
